import { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, X, Send, Loader2, Paperclip } from 'lucide-react';
import DOMPurify from 'dompurify';
import { getVisitorId, setVisitorId, clearVisitorId } from './storage';
import {
  getWidgetConfig,
  createVisitor,
  getOpenConversation,
  createOrGetConversation,
  getWidgetMessages,
  sendWidgetMessage,
  updateWidgetMessage,
  updateVisitorPresence,
  sendTypingStatus,
  markConversationRead,
  type MessageSender,
} from '../api/widget';
import { getEcho, type MessageSentPayload, type MessageUpdatedPayload } from '../echo';
import { isImageOnlyMessage, stripHtml, hasAttachment } from '../utils/stripHtml';
import { useIsMobile } from '../utils/breakpoints';
import MessageBubble from './MessageBubble';

const EDIT_WINDOW_MS = 5 * 60 * 1000;
import {
  requestNotificationPermission,
  showSystemNotification,
  NOTIFICATION_OPEN_WIDGET_EVENT,
} from '../utils/systemNotification';
import {
  Root,
  Panel,
  Header,
  HeaderRow,
  Title,
  CloseBtn,
  Subtitle,
  PresenceDot,
  Body,
  DropOverlay,
  DropIcon,
  DropText,
  ErrorPara,
  LoadingWrap,
  LoadingDot,
  LeadForm,
  LeadInput,
  Messages,
  MessageGroup,
  SenderAvatar,
  AvatarSpacer,
  BubbleColumn,
  SenderName,
  WelcomeCard,
  WelcomeIcon,
  WelcomeText,
  ReplyPreviewBanner,
  ReplyPreviewText,
  ReplyPreviewSender,
  ReplyPreviewSnippet,
  ReplyPreviewClose,
  ComposerForm,
  Composer,
  AttachWrap,
  AttachLabel,
  HiddenFileInput,
  MessageTextarea,
  AttachmentPill,
  AttachmentBadgePdf,
  AttachmentThumb,
  AttachmentThumbImg,
  AttachmentName,
  AttachmentRemove,
  SendBtn,
  ModalOverlay,
  ModalContentImageWrap,
  ImageModalClose,
  ImageModalCloseIcon,
  ModalImage,
  PdfModal,
  PdfHeader,
  PdfTitle,
  PdfCloseBtn,
  PdfCloseIcon,
  PdfFrame,
  FabWrap,
  Fab,
  UnreadBadge,
  SpinIcon,
} from './ChatWidget.styles';

export interface ChatWidgetProps {
  /** Token du site (obligatoire pour l’API widget) */
  siteToken: string;
  /** Couleur principale (surchargée par la config API si présente) */
  color?: string;
  /** Position de la bulle (surchargée par la config API si présente) */
  position?: 'left' | 'right';
  /** Message d'accueil (surchargé par la config API si présente) */
  welcomeMessage?: string;
  /** Afficher "agent en ligne" (surchargé par la config API si présente) */
  agentsOnline?: boolean;
  /** Force couleur/position (prioritaire sur la config API) — utilisé pour l'aperçu live dans Sites.tsx pendant l'édition. */
  previewOverrides?: { color?: string; position?: 'left' | 'right' };
}

interface DisplayMessage {
  id: string;
  content: string;
  sender_type: 'visitor' | 'agent' | 'system';
  created_at: string;
  sender?: MessageSender;
  reply_to?: { id: string; content: string; sender_type: string; sender_name?: string | null } | null;
  edited_at?: string | null;
  original_content?: string | null;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
}

function sameSender(a: DisplayMessage, b: DisplayMessage): boolean {
  if (a.sender_type !== b.sender_type) return false;
  if (a.sender_type === 'agent') return a.sender?.id === b.sender?.id;
  return true;
}

export default function ChatWidget({
  siteToken,
  color: colorProp = '#2563eb',
  position: positionProp = 'right',
  welcomeMessage: welcomeMessageProp = 'Bonjour, comment pouvons-nous vous aider ?',
  agentsOnline: agentsOnlineProp = true,
  previewOverrides,
}: ChatWidgetProps) {
  const [open, setOpen] = useState(false);
  const [visitorId, setVisitorIdState] = useState<number | null>(() => getVisitorId(siteToken));
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [replyingTo, setReplyingTo] = useState<DisplayMessage | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [, setEditWindowTick] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [config, setConfig] = useState<{
    siteId?: number;
    color: string;
    position: 'left' | 'right';
    welcomeMessage: string;
    agentsOnline: boolean;
  } | null>(null);
  const [configReady, setConfigReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
  const focusAfterSendRef = useRef(false);
  const openRef = useRef(open);
  const typingStoppedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTypingSentRef = useRef<number>(0);
  const TYPING_THROTTLE_MS = 2000;
  const TYPING_STOPPED_DELAY_MS = 3000;
  const isMobile = useIsMobile();

  const color = previewOverrides?.color ?? config?.color ?? colorProp;
  const position = previewOverrides?.position ?? config?.position ?? positionProp;
  const welcomeMessage = config?.welcomeMessage ?? welcomeMessageProp;
  const agentsOnline = config?.agentsOnline ?? agentsOnlineProp;

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const loadConfig = useCallback(async () => {
    try {
      const data = await getWidgetConfig(siteToken);
      setConfig({
        siteId: data.site_id,
        color: data.widget_color ?? colorProp,
        position: (data.widget_position as 'left' | 'right') ?? positionProp,
        welcomeMessage: data.welcome_message ?? welcomeMessageProp,
        agentsOnline: data.has_online_agent ?? agentsOnlineProp,
      });
    } catch {
      setConfig(null);
    } finally {
      setConfigReady(true);
    }
  }, [siteToken, colorProp, positionProp, welcomeMessageProp, agentsOnlineProp]);

  const toDisplayMessages = (list: Awaited<ReturnType<typeof getWidgetMessages>>): DisplayMessage[] =>
    list.map((m) => ({
      id: String(m.id),
      content: m.content,
      sender_type: m.sender_type as DisplayMessage['sender_type'],
      created_at: m.created_at,
      sender: m.sender,
      reply_to: m.reply_to
        ? {
            id: String(m.reply_to.id),
            content: m.reply_to.content,
            sender_type: m.reply_to.sender_type,
            sender_name: m.reply_to.sender_name ?? null,
          }
        : null,
      edited_at: m.edited_at ?? null,
      original_content: m.original_content ?? null,
    }));

  const loadConversationAndMessages = useCallback(async () => {
    if (!visitorId || !siteToken) return;
    setLoading(true);
    setError(null);
    try {
      const conv = await getOpenConversation(siteToken, visitorId);
      if (conv) {
        setConversationId(conv.id);
        const list = await getWidgetMessages(siteToken, conv.id);
        setMessages(toDisplayMessages(list));
      } else {
        setConversationId(null);
        setMessages([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur chargement');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [siteToken, visitorId]);

  // Charger la config au montage pour appliquer couleur/position à la bulle avant d’ouvrir le chat
  useEffect(() => {
    if (siteToken) loadConfig();
  }, [siteToken, loadConfig]);

  // Temps réel : présence des agents (Un agent est en ligne / Aucun agent en ligne)
  useEffect(() => {
    const siteId = config?.siteId;
    if (siteId == null) return;
    const echo = getEcho();
    if (!echo) return;
    const channel = echo.channel(`site.${siteId}`);
    channel.listen('.agent.presence', (e: { has_online_agent: boolean }) => {
      setConfig((prev) => (prev ? { ...prev, agentsOnline: e.has_online_agent } : null));
    });
    return () => {
      echo.leave(`site.${siteId}`);
    };
  }, [config?.siteId]);

  // Rafraîchir la config tant que le chat est ouvert (repli toutes les 25s)
  useEffect(() => {
    if (open && siteToken) {
      loadConfig();
      const interval = setInterval(loadConfig, 25_000);
      return () => clearInterval(interval);
    }
  }, [open, siteToken, loadConfig]);

  // Charger la conversation existante dès qu'on connaît le visiteur (pas seulement à l'ouverture) :
  // permet de recevoir les messages en temps réel et de compter les non-lus même bulle fermée.
  useEffect(() => {
    if (visitorId) {
      loadConversationAndMessages();
    } else {
      setConversationId(null);
      setMessages([]);
    }
  }, [visitorId, loadConversationAndMessages]);

  // Temps réel : écouter les nouveaux messages (visiteur ou agent), même bulle fermée (badge non-lus).
  useEffect(() => {
    if (!conversationId) return;
    const echo = getEcho();
    if (!echo) return;
    const channel = echo.channel(`conversation.${conversationId}`);
    channel.listen('.message.sent', (e: MessageSentPayload) => {
      const m = e.message;
      setMessages((prev) => {
        if (prev.some((x) => x.id === String(m.id))) return prev;
        return [
          ...prev,
          {
            id: String(m.id),
            content: m.content,
            sender_type: m.sender_type as DisplayMessage['sender_type'],
            created_at: m.created_at,
            sender: m.sender,
            reply_to: m.reply_to
              ? {
                  id: String(m.reply_to.id),
                  content: m.reply_to.content,
                  sender_type: m.reply_to.sender_type,
                  sender_name: m.reply_to.sender_name ?? null,
                }
              : null,
          },
        ];
      });
      if (m.sender_type === 'agent' && !openRef.current) {
        setUnreadCount((n) => n + 1);
        const preview = stripHtml(m.content).trim();
        showSystemNotification(m.sender?.name ?? 'Nouveau message', preview || 'Vous avez reçu un nouveau message.', {
          tag: `crispclone-widget-message-${m.id}`,
          openWidget: true,
        });
      } else if (m.sender_type === 'agent' && openRef.current) {
        markConversationRead(siteToken, conversationId).catch(() => {});
      }
    });
    channel.listen('.message.updated', (e: MessageUpdatedPayload) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === String(e.message_id)
            ? { ...msg, content: e.content, original_content: e.original_content, edited_at: e.edited_at }
            : msg
        )
      );
    });
    return () => {
      echo.leave(`conversation.${conversationId}`);
    };
  }, [conversationId]);

  // Force une réévaluation périodique de la fenêtre d'édition de 5 min tant que le widget est ouvert.
  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => setEditWindowTick((t) => t + 1), 20_000);
    return () => clearInterval(interval);
  }, [open]);

  // Ouverture du widget depuis le clic sur une notification système
  useEffect(() => {
    const onOpenWidget = () => setOpen(true);
    window.addEventListener(NOTIFICATION_OPEN_WIDGET_EVENT, onOpenWidget);
    return () => window.removeEventListener(NOTIFICATION_OPEN_WIDGET_EVENT, onOpenWidget);
  }, []);

  // Toujours scroller en bas des messages dans le widget quand la liste change
  useEffect(() => {
    if (!open) return;
    const el = messagesContainerRef.current;
    if (!el) return;
    const t = setTimeout(() => {
      el.scrollTop = el.scrollHeight;
    }, 0);
    // Les images des pièces jointes chargent après coup : re-ancrer en bas tant que ça se produit.
    const onLoad = () => {
      el.scrollTop = el.scrollHeight;
    };
    el.addEventListener('load', onLoad, true);
    return () => {
      clearTimeout(t);
      el.removeEventListener('load', onLoad, true);
    };
  }, [open, messages.length]);

  const handleQuoteClick = (messageId: string) => {
    const el = document.getElementById(`widget-msg-${messageId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedMessageId(messageId);
    window.setTimeout(() => setHighlightedMessageId((cur) => (cur === messageId ? null : cur)), 1500);
  };

  // Une conversation widget n'affiche que les messages du visiteur courant : tout message
  // sender_type === 'visitor' lui appartient, pas besoin de comparer un sender_id.
  const canEditMessage = (m: DisplayMessage): boolean =>
    m.sender_type === 'visitor' &&
    !m.edited_at &&
    !hasAttachment(m.content) &&
    Date.now() - new Date(m.created_at).getTime() < EDIT_WINDOW_MS;

  const handleStartEdit = (m: DisplayMessage) => {
    setEditingMessageId(m.id);
    setEditingContent(m.content);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingContent('');
  };

  const handleSaveEdit = () => {
    if (!conversationId || !visitorId || !editingMessageId || savingEdit) return;
    if (!stripHtml(editingContent).trim()) return;
    setSavingEdit(true);
    updateWidgetMessage(siteToken, conversationId, Number(editingMessageId), visitorId, editingContent)
      .then((updated) => {
        setMessages((prev) =>
          prev.map((x) =>
            x.id === String(updated.id)
              ? { ...x, content: updated.content, edited_at: updated.edited_at ?? null, original_content: updated.original_content ?? null }
              : x
          )
        );
        setEditingMessageId(null);
        setEditingContent('');
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Erreur modification'))
      .finally(() => setSavingEdit(false));
  };

  const handleWidgetMessageImageClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.tagName === 'IMG') {
      const img = target as HTMLImageElement;
      if (img.src) {
        event.preventDefault();
        event.stopPropagation();
        setPreviewImageUrl(img.src);
      }
      return;
    }
    const link = target.closest('a[href]') as HTMLAnchorElement | null;
    if (link?.href && (link.getAttribute('href')?.toLowerCase().includes('.pdf') || link.closest('.message-attachment-pdf'))) {
      event.preventDefault();
      event.stopPropagation();
      setPreviewPdfUrl(link.href);
    }
  };

  // Présence visiteur : active quand le widget est ouvert, left quand il est fermé (pour l’Inbox dashboard)
  useEffect(() => {
    if (!siteToken || !visitorId) return;
    const status = open ? 'active' : 'left';
    updateVisitorPresence(siteToken, visitorId, status).catch(() => {
      // Ignorer les erreurs (ex. 403 si le visiteur n’a pas encore de conversation sur ce site)
    });
  }, [siteToken, visitorId, open]);

  // Remettre le focus sur le champ message après envoi (une fois sending repassé à false et DOM à jour)
  useEffect(() => {
    if (!sending && focusAfterSendRef.current && open && !loading) {
      focusAfterSendRef.current = false;
      const t = setTimeout(() => messageInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [sending, open, loading]);

  // Ajuster la hauteur de la textarea quand le message change (dont après envoi quand on vide)
  useEffect(() => {
    const ta = messageInputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, [message]);

  const sendTypingStopped = useCallback(() => {
    if (typingStoppedTimeoutRef.current) {
      clearTimeout(typingStoppedTimeoutRef.current);
      typingStoppedTimeoutRef.current = null;
    }
    if (conversationId != null && visitorId != null && siteToken) {
      sendTypingStatus(siteToken, conversationId, visitorId, 'stopped').catch(() => {});
    }
  }, [conversationId, visitorId, siteToken]);

  const onMessageChange = useCallback(
    (value: string) => {
      setMessage(value);
      if (conversationId == null || visitorId == null || !siteToken) return;
      if (typingStoppedTimeoutRef.current) {
        clearTimeout(typingStoppedTimeoutRef.current);
        typingStoppedTimeoutRef.current = null;
      }
      const now = Date.now();
      if (now - lastTypingSentRef.current >= TYPING_THROTTLE_MS) {
        lastTypingSentRef.current = now;
        sendTypingStatus(siteToken, conversationId, visitorId, 'typing').catch(() => {});
      }
      typingStoppedTimeoutRef.current = setTimeout(() => {
        typingStoppedTimeoutRef.current = null;
        sendTypingStatus(siteToken, conversationId, visitorId, 'stopped').catch(() => {});
      }, TYPING_STOPPED_DELAY_MS);
    },
    [conversationId, visitorId, siteToken]
  );

  useEffect(() => {
    if (!open) sendTypingStopped();
  }, [open, sendTypingStopped]);

  const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

  const handleAttachmentFile = useCallback((file: File | null) => {
    if (!file) {
      setAttachment(null);
      return;
    }
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';
    if (!isImage && !isPdf) {
      setError('Format accepté : image ou PDF (max 5 Mo).');
      setAttachment(null);
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError("La pièce jointe dépasse la taille maximale de 5 Mo.");
      setAttachment(null);
      return;
    }
    setError(null);
    setAttachment(file);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDraggingOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleAttachmentFile(file);
    },
    [handleAttachmentFile]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files')) setIsDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDraggingOver(false);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() && !attachment) return;
    if (messages.length === 0 && !firstName.trim()) {
      setError('Merci de renseigner votre prénom.');
      return;
    }
    if (messages.length === 0 && !EMAIL_REGEX.test(email.trim())) {
      setError('Merci de renseigner une adresse email valide.');
      return;
    }
    setError(null);
    sendTypingStopped();

    let vid = visitorId;
    let cid = conversationId;

    // Si aucune conversation ouverte mais qu'un visitor existe déjà,
    // on considère qu'il s'agit d'un nouveau visiteur sur ce site.
    if (vid != null && cid == null) {
      clearVisitorId(siteToken);
      setVisitorIdState(null);
      vid = null;
    }

    try {
      setSending(true);
      if (vid == null) {
        const { id } = await createVisitor(siteToken, {
          first_name: firstName.trim() || undefined,
          email: email.trim() || undefined,
        });
        setVisitorId(siteToken, id);
        setVisitorIdState(id);
        vid = id;
      }
      if (cid == null) {
        const { id } = await createOrGetConversation(siteToken, {
          visitor_id: vid,
          first_name: firstName.trim() || undefined,
          email: email.trim() || undefined,
        });
        setConversationId(id);
        cid = id;
      }
      const replyToId = replyingTo ? Number(replyingTo.id) : null;
      await sendWidgetMessage(siteToken, cid, vid, message.trim(), attachment, replyToId);
      setMessage('');
      setAttachment(null);
      setReplyingTo(null);
      const list = await getWidgetMessages(siteToken, cid);
      setMessages(toDisplayMessages(list));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur envoi');
    } finally {
      focusAfterSendRef.current = true;
      setSending(false);
    }
  };

  const isLeft = position === 'left';

  if (!configReady) return null;

  return (
    <Root
      $isLeft={isLeft}
      style={{ zIndex: 999999, ['--crispclone-color' as unknown as keyof React.CSSProperties]: color }}
      data-position={position}
    >
      {open && (
        <Panel $isLeft={isLeft} role="dialog" aria-label="Chat">
          <Header>
            <HeaderRow>
              <Title>Chat</Title>
              <CloseBtn type="button" onClick={() => setOpen(false)} aria-label="Fermer">
                <X size={16} strokeWidth={2.5} />
              </CloseBtn>
            </HeaderRow>
            <Subtitle>
              {agentsOnline ? (
                <>
                  <PresenceDot $online />
                  Un agent est en ligne
                </>
              ) : (
                <>
                  <PresenceDot $online={false} />
                  Aucun agent en ligne
                </>
              )}
            </Subtitle>
          </Header>
          <Body
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isDraggingOver && (
              <DropOverlay aria-hidden>
                <DropIcon fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.8}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </DropIcon>
                <DropText>Déposez l&apos;image pour l&apos;envoyer</DropText>
              </DropOverlay>
            )}
            {error && <ErrorPara>{error}</ErrorPara>}
            {loading && (
              <LoadingWrap aria-label="Connexion en cours">
                <LoadingDot $delay={0} />
                <LoadingDot $delay={150} />
                <LoadingDot $delay={300} />
              </LoadingWrap>
            )}
            {!loading && (
              <>
                {messages.length === 0 && (
                  <LeadForm>
                    <LeadInput
                      type="text"
                      placeholder="Prénom"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                    <LeadInput
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </LeadForm>
                )}
                <Messages ref={messagesContainerRef} onClick={handleWidgetMessageImageClick}>
                  {welcomeMessage && (
                    <WelcomeCard>
                      <WelcomeIcon aria-hidden>👋</WelcomeIcon>
                      <WelcomeText>{welcomeMessage}</WelcomeText>
                    </WelcomeCard>
                  )}
                  {messages.map((m, i) => {
                    const sanitizedHtml = DOMPurify.sanitize(
                      m.content.includes('<') ? m.content : m.content.replace(/\n/g, '<br>'),
                      { ADD_ATTR: ['target', 'rel', 'download', 'class', 'aria-hidden'] }
                    );
                    const imageOnly = isImageOnlyMessage(sanitizedHtml);
                    const isVisitor = m.sender_type === 'visitor';
                    const isAgent = m.sender_type === 'agent';
                    const prev = messages[i - 1];
                    const next = messages[i + 1];
                    const isFirstInGroup = !prev || !sameSender(m, prev);
                    const isLastInGroup = !next || !sameSender(m, next);
                    const showAvatar = isAgent && isFirstInGroup;

                    return (
                      <MessageGroup key={m.id} $isVisitor={isVisitor}>
                        {isAgent &&
                          (showAvatar ? (
                            <SenderAvatar $bg={m.sender?.avatar ? undefined : '#e2e8f0'} title={m.sender?.name}>
                              {m.sender?.avatar ? (
                                <img src={m.sender.avatar} alt="" />
                              ) : (
                                <span style={{ color: '#475569' }}>{initials(m.sender?.name ?? '?')}</span>
                              )}
                            </SenderAvatar>
                          ) : (
                            <AvatarSpacer />
                          ))}
                        <BubbleColumn $isVisitor={isVisitor}>
                          {showAvatar && m.sender?.name && <SenderName>{m.sender.name}</SenderName>}
                          <MessageBubble
                            message={m}
                            isVisitor={isVisitor}
                            imageOnly={imageOnly}
                            grouped={!isLastInGroup}
                            sanitizedHtml={sanitizedHtml}
                            enableSwipe={isMobile}
                            highlighted={highlightedMessageId === m.id}
                            onReply={setReplyingTo}
                            onQuoteClick={handleQuoteClick}
                            canEdit={canEditMessage(m)}
                            isEditing={editingMessageId === m.id}
                            editingValue={editingContent}
                            onEditingChange={setEditingContent}
                            onStartEdit={handleStartEdit}
                            onSaveEdit={handleSaveEdit}
                            onCancelEdit={handleCancelEdit}
                            savingEdit={savingEdit}
                          />
                        </BubbleColumn>
                      </MessageGroup>
                    );
                  })}
                </Messages>
                <ComposerForm onSubmit={handleSubmit}>
                  {replyingTo && (
                    <ReplyPreviewBanner>
                      <ReplyPreviewText>
                        <ReplyPreviewSender>
                          {replyingTo.sender_type === 'agent' ? replyingTo.sender?.name ?? 'Agent' : 'Vous'}
                        </ReplyPreviewSender>
                        <ReplyPreviewSnippet>{stripHtml(replyingTo.content).slice(0, 140)}</ReplyPreviewSnippet>
                      </ReplyPreviewText>
                      <ReplyPreviewClose type="button" aria-label="Annuler la réponse" onClick={() => setReplyingTo(null)}>
                        <X size={15} />
                      </ReplyPreviewClose>
                    </ReplyPreviewBanner>
                  )}
                  <Composer>
                    <AttachWrap>
                      <AttachLabel>
                        <Paperclip size={17} strokeWidth={1.8} aria-hidden="true" />
                        <HiddenFileInput
                          type="file"
                          accept="image/*,application/pdf"
                          aria-label="Choisir une image ou un PDF à envoyer"
                          title="Choisir une image ou un PDF à envoyer"
                          onChange={(e) => handleAttachmentFile(e.target.files?.[0] ?? null)}
                        />
                      </AttachLabel>
                    </AttachWrap>
                    <MessageTextarea
                      ref={messageInputRef}
                      rows={1}
                      placeholder="Entrez votre message..."
                      value={message}
                      onChange={(e) => {
                        const ta = e.target;
                        const value = ta.value;
                        onMessageChange(value);
                        ta.style.height = 'auto';
                        ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmit(e);
                        }
                      }}
                      disabled={sending}
                    />
                    {attachment && (
                      <AttachmentPill type="button" onClick={() => setAttachment(null)}>
                        {attachment.type === 'application/pdf' ? (
                          <AttachmentBadgePdf aria-hidden>PDF</AttachmentBadgePdf>
                        ) : (
                          <AttachmentThumb>
                            <AttachmentThumbImg
                              src={URL.createObjectURL(attachment)}
                              alt="Aperçu"
                            />
                          </AttachmentThumb>
                        )}
                        <AttachmentName title={attachment.name}>{attachment.name}</AttachmentName>
                        <AttachmentRemove aria-hidden>
                          <X size={11} strokeWidth={2.5} />
                        </AttachmentRemove>
                      </AttachmentPill>
                    )}
                    <SendBtn
                      type="submit"
                      disabled={
                        sending ||
                        (!message.trim() && !attachment) ||
                        (messages.length === 0 && !firstName.trim()) ||
                        (messages.length === 0 && !EMAIL_REGEX.test(email.trim()))
                      }
                      aria-label="Envoyer le message"
                    >
                      {sending ? (
                        <SpinIcon>
                          <Loader2 size={16} strokeWidth={2.5} />
                        </SpinIcon>
                      ) : (
                        <Send size={16} strokeWidth={2.25} />
                      )}
                    </SendBtn>
                  </Composer>
                </ComposerForm>
                {previewImageUrl && (
                  <ModalOverlay onClick={() => setPreviewImageUrl(null)} aria-modal="true" role="dialog">
                    <ModalContentImageWrap onClick={(e) => e.stopPropagation()}>
                      <ImageModalClose
                        type="button"
                        onClick={() => setPreviewImageUrl(null)}
                        aria-label="Fermer"
                      >
                        <ImageModalCloseIcon fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </ImageModalCloseIcon>
                      </ImageModalClose>
                      <ModalImage src={previewImageUrl} alt="Image du message" />
                    </ModalContentImageWrap>
                  </ModalOverlay>
                )}

                {previewPdfUrl && (
                  <ModalOverlay
                    $padded
                    onClick={() => setPreviewPdfUrl(null)}
                    aria-modal="true"
                    role="dialog"
                  >
                    <PdfModal onClick={(e) => e.stopPropagation()}>
                      <PdfHeader>
                        <PdfTitle>Aperçu du document</PdfTitle>
                        <PdfCloseBtn type="button" onClick={() => setPreviewPdfUrl(null)} aria-label="Fermer">
                          <PdfCloseIcon fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </PdfCloseIcon>
                        </PdfCloseBtn>
                      </PdfHeader>
                      <PdfFrame src={previewPdfUrl} title="Aperçu PDF" />
                    </PdfModal>
                  </ModalOverlay>
                )}
              </>
            )}
          </Body>
        </Panel>
      )}
      <FabWrap>
        <Fab
          type="button"
          onClick={() => {
            const next = !open;
            setOpen(next);
            if (next) {
              setUnreadCount(0);
              requestNotificationPermission().catch(() => {});
              if (conversationId != null) {
                markConversationRead(siteToken, conversationId).catch(() => {});
              }
            }
          }}
          aria-label={open ? 'Fermer le chat' : 'Ouvrir le chat'}
        >
          {open ? <X size={26} strokeWidth={2.25} /> : <MessageSquare size={26} strokeWidth={2.25} />}
        </Fab>
        {!open && unreadCount > 0 && <UnreadBadge>{unreadCount > 9 ? '9+' : unreadCount}</UnreadBadge>}
      </FabWrap>
    </Root>
  );
}
